import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import AppLayout from "@/components/AppLayout";
import Recipes from "@/pages/Recipes";
import RecipeDetail from "@/pages/RecipeDetail";
import ShoppingLists from "@/pages/ShoppingLists";
import ShoppingListDetail from "@/pages/ShoppingListDetail";
import Planner from "@/pages/Planner";
import Pantry from "@/pages/Pantry";
import Home from "@/pages/Home";
import FoodScanner from "@/pages/FoodScanner";
import ProductLibrary from "@/pages/ProductLibrary";
import Profile from "@/pages/Profile";
import SharedRecipe from "@/pages/SharedRecipe";
import SharedShoppingList from "@/pages/SharedShoppingList";
import Scan from "@/pages/Scan";
import CalorieTarget from "@/pages/CalorieTarget";
import WeightTracker from "@/pages/WeightTracker";
import Discover from "@/pages/Discover";
import Admin from "@/pages/Admin";
import OwnerOverview from "@/pages/OwnerOverview";
import OwnerPlansPage from "@/pages/OwnerPlansPage";
import OwnerCouponsPage from "@/pages/OwnerCouponsPage";
import OwnerMessages from "@/pages/OwnerMessages";
import OwnerStockRequests from "@/pages/OwnerStockRequests";
import OwnerProductLibrary from "@/pages/OwnerProductLibrary";
import Billing from "@/pages/Billing";
import BillingReturn from "@/pages/BillingReturn";
import Friends from "@/pages/Friends";
import FriendsInvite from "@/pages/FriendsInvite";
import OwnerLayout from "@/components/OwnerLayout";
import OwnerLogin from "@/pages/OwnerLogin";
import OwnerAccount from "@/pages/OwnerAccount";
import ExtensionLanding from "@/pages/ExtensionLanding";
import ExtensionPrivacy from "@/pages/ExtensionPrivacy";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import ProUpsellModal from "@/components/ProUpsellModal";
import { startVersionCheck } from "@/lib/versionCheck";
import { preloadCriticalImages } from "@/lib/preloadImages";
import SplashScreen from "@/components/SplashScreen";
import InstallAppBanner from "@/components/InstallAppBanner";
import "@/lib/installCameraErrorGuard";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[#5C6B61]">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Catch Emergent OAuth callback (URL fragment) before normal routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/share/list/:token" element={<SharedShoppingList />} />
      <Route path="/share/:token" element={<SharedRecipe />} />
      <Route path="/i/:code" element={<FriendsInvite />} />
      <Route path="/extension" element={<ExtensionLanding />} />
      <Route path="/privacy-extension" element={<ExtensionPrivacy />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/app" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<Home />} />
        <Route path="recipes" element={<Recipes />} />
        <Route path="recipes/:id" element={<RecipeDetail />} />
        <Route path="shopping" element={<ShoppingLists />} />
        <Route path="shopping/:id" element={<ShoppingListDetail />} />
        <Route path="planner" element={<Planner />} />
        <Route path="pantry" element={<Pantry />} />
        <Route path="food-scanner" element={<FoodScanner />} />
        <Route path="library" element={<ProductLibrary />} />
        <Route path="discover" element={<Discover />} />
        <Route path="scan" element={<Scan />} />
        <Route path="calories" element={<CalorieTarget />} />
        <Route path="weight" element={<WeightTracker />} />
        <Route path="billing" element={<Billing />} />
        <Route path="billing/return" element={<BillingReturn />} />
        <Route path="friends" element={<Friends />} />
        <Route path="friends/invite/:code" element={<FriendsInvite />} />
        <Route path="profile" element={<Profile />} />
      </Route>
      <Route path="/owner/login" element={<OwnerLogin />} />
      <Route path="/owner" element={<OwnerLayout />}>
        <Route index element={<OwnerOverview />} />
        <Route path="users" element={<Admin />} />
        <Route path="messages" element={<OwnerMessages />} />
        <Route path="stock-requests" element={<OwnerStockRequests />} />
        <Route path="product-library" element={<OwnerProductLibrary />} />
        <Route path="plans" element={<OwnerPlansPage />} />
        <Route path="coupons" element={<OwnerCouponsPage />} />
        <Route path="account" element={<OwnerAccount />} />
      </Route>
      <Route path="/" element={<Navigate to="/app" replace />} />
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    startVersionCheck();
    preloadCriticalImages();
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <SplashScreen />
        <AppRouter />
        <ProUpsellModal />
        <InstallAppBanner />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
